import { useMemo } from 'react'

import { isTemplateInCampaign } from '../campaigns/utils'
import { GOALS, STRUCTURES, TYPES, USER_TYPES } from '../types'
import type {
  Goal,
  Structure,
  Template,
  TemplateCampaign,
  Type,
  UserType,
} from '../types'

type SearchableTemplate = Pick<
  Template,
  'body' | 'description' | 'goal' | 'tags' | 'title' | 'type'
> &
  Partial<Pick<Template, 'linkedCampaigns' | 'structure' | 'userTypes'>>

const TYPE_SET: ReadonlySet<string> = new Set(TYPES)
const USER_TYPE_SET: ReadonlySet<string> = new Set(USER_TYPES)
const STRUCTURE_SET: ReadonlySet<string> = new Set(STRUCTURES)
const GOAL_SET: ReadonlySet<string> = new Set(GOALS)

// Owner tokens shown on the My Templates tab and matched against
// `CustomTemplate.owner` here via a duck-type read. Buffer-curated templates
// don't carry an `owner` field, so explore-tab searches never see them.
// Exported as the single source for both the filter UIs and this matcher.
export type OwnerToken = 'mine' | 'team'
export const MINE_OWNER_TOKENS: readonly OwnerToken[] = ['mine', 'team']
const OWNER_SET: ReadonlySet<string> = new Set(MINE_OWNER_TOKENS)

const ownerOf = (template: SearchableTemplate): string | undefined => {
  if (!('owner' in template)) return undefined
  const value = template.owner
  return typeof value === 'string' ? value : undefined
}

const matchesQuery = (template: SearchableTemplate, query: string): boolean => {
  if (!query) return true
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return (
    template.title.toLowerCase().includes(needle) ||
    template.description.toLowerCase().includes(needle) ||
    template.body.toLowerCase().includes(needle) ||
    template.type.toLowerCase().includes(needle) ||
    template.goal.toLowerCase().includes(needle) ||
    template.tags.some((tag) => tag.toLowerCase().includes(needle))
  )
}

const matchesFilters = (
  template: SearchableTemplate,
  filters: string[],
  activeCampaigns: TemplateCampaign[],
): boolean => {
  if (filters.length === 0) return true

  const typeFilters = filters.filter((f): f is Type => TYPE_SET.has(f))
  const userTypeFilters = filters.filter((f): f is UserType =>
    USER_TYPE_SET.has(f),
  )
  const structureFilters = filters.filter((f): f is Structure =>
    STRUCTURE_SET.has(f),
  )
  const goalFilters = filters.filter((f): f is Goal => GOAL_SET.has(f))
  const ownerFilters = filters.filter((f) => OWNER_SET.has(f))
  const campaignFilters = filters.filter(
    (f) =>
      !TYPE_SET.has(f) &&
      !USER_TYPE_SET.has(f) &&
      !STRUCTURE_SET.has(f) &&
      !GOAL_SET.has(f) &&
      !OWNER_SET.has(f),
  )

  if (ownerFilters.length > 0) {
    const owner = ownerOf(template)
    if (!owner || !ownerFilters.includes(owner)) {
      return false
    }
  }
  if (typeFilters.length > 0 && !typeFilters.includes(template.type)) {
    return false
  }
  if (
    userTypeFilters.length > 0 &&
    !userTypeFilters.some((u) => template.userTypes?.includes(u))
  ) {
    return false
  }
  if (
    structureFilters.length > 0 &&
    !structureFilters.some((structure) => template.structure === structure)
  ) {
    return false
  }
  if (goalFilters.length > 0 && !goalFilters.includes(template.goal)) {
    return false
  }
  if (campaignFilters.length > 0) {
    const matchedCampaigns = activeCampaigns.filter((c) =>
      campaignFilters.includes(c.name),
    )
    if (
      matchedCampaigns.length === 0 ||
      !matchedCampaigns.some((c) => isTemplateInCampaign(template, c))
    ) {
      return false
    }
  }
  return true
}

/**
 * Unified search/filter pass: takes a free-text query plus a flat list of
 * filter tokens (campaign name | type | userType | structure | goal) and
 * returns the matching templates in original order.
 */
export const useTemplateSearch = <T extends SearchableTemplate>({
  templates,
  query,
  filters,
  activeCampaigns,
}: {
  templates: T[]
  query: string
  filters: string[]
  activeCampaigns: TemplateCampaign[]
}): T[] => {
  return useMemo(() => {
    return templates.filter(
      (t) =>
        matchesQuery(t, query) && matchesFilters(t, filters, activeCampaigns),
    )
  }, [templates, query, filters, activeCampaigns])
}
